import { MusicalNote } from "../MusicalNote";
import {
  HiHatEighthsPattern,
  HiHatQuartersPattern,
  KickSnareFunkPattern,
  KickSnareRockPattern,
  LargeCrashFillPattern,
  MediumTomFillPattern,
  SmallSnareFillPattern,
} from "./DrumPatterns";
import {
  MasterComposer,
  MusicalComposer,
  MusicalContext,
} from "./MasterComposer";
import {
  FillPattern,
  HiHatPattern,
  KickSnarePattern,
} from "./PatternInterfaces";
import { PhraseManager } from "./PhraseManager";

/**
 * Drums composer that generates phrase-aware drum patterns.
 *
 * Uses the pattern system with separate hi-hat and kick/snare patterns
 * for groove portions of phrases, and dedicated fill patterns for phrase endings.
 * Pattern selection is stateless and deterministic based on phrase seed.
 *
 * Musical Approach:
 * - Groove portions: Combine hi-hat + kick/snare patterns for stable foundation
 * - Fill portions: Use transitional patterns that conclude the phrase
 * - Stateless pattern selection, intensity affects execution
 * - Separate pattern libraries for different drum elements
 */
export class DrumsComposer implements MusicalComposer {
  // Pattern libraries for each drum element (immutable)
  private readonly hiHatPatterns: HiHatPattern[] = [
    new HiHatQuartersPattern(),
    new HiHatEighthsPattern(),
  ];

  private readonly kickSnarePatterns: KickSnarePattern[] = [
    new KickSnareRockPattern(),
    new KickSnareFunkPattern(),
  ];

  private readonly fillPatterns: {
    readonly small: FillPattern[];
    readonly medium: FillPattern[];
    readonly large: FillPattern[];
  } = {
    small: [new SmallSnareFillPattern()],
    medium: [new MediumTomFillPattern()],
    large: [new LargeCrashFillPattern()],
  };

  /**
   * Generate drum notes for the next beat based on musical context.
   * Stateless - selects and executes patterns based on current context.
   */
  generateNotesForBeat(context: MusicalContext): MusicalNote[] {
    if (PhraseManager.isInFill(context.timingInfo)) {
      return this.executeFillPattern(context);
    } else {
      return this.executeGroovePatterns(context);
    }
  }

  /**
   * Execute groove patterns (hi-hat + kick/snare) based on phrase seed.
   */
  private executeGroovePatterns(context: MusicalContext): MusicalNote[] {
    const notes: MusicalNote[] = [];
    const { timingInfo } = context;

    // Select hi-hat pattern based on phrase seed
    const hiHatIndex = timingInfo.phraseSeed % this.hiHatPatterns.length;
    const hiHatPattern = this.hiHatPatterns[hiHatIndex];

    const hiHatNotes = hiHatPattern.execute(timingInfo, context.intensity);
    notes.push(...hiHatNotes);

    // Select kick/snare pattern (use different calculation for variety)
    const kickSnareIndex =
      Math.floor(timingInfo.phraseSeed / 2) % this.kickSnarePatterns.length;
    const kickSnarePattern = this.kickSnarePatterns[kickSnareIndex];

    const kickSnareNotes = kickSnarePattern.execute(
      timingInfo,
      context.intensity,
    );
    notes.push(...kickSnareNotes);

    return notes;
  }

  /**
   * Execute fill pattern based on fill type and phrase seed.
   */
  private executeFillPattern(context: MusicalContext): MusicalNote[] {
    const { timingInfo } = context;

    // Select fill pattern based on fill type and phrase seed
    const fillOptions = this.fillPatterns[timingInfo.fillType];
    const fillIndex = timingInfo.phraseSeed % fillOptions.length;
    const fillPattern = fillOptions[fillIndex];

    return fillPattern.execute(timingInfo, context.intensity);
  }
}
